import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable } from 'rxjs';
import { finalize, map, shareReplay } from 'rxjs/operators';
import { AppService } from '@app/services/app.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AuthService } from '@app/services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { ClientSideSingleSelectionConfig, ServerSideSingleSelectionConfig } from '@servicemind.tis/tis-search-and-select-dropdown';
import { HelperService } from '@app/services/helper.service';

@Component({
  selector: 'app-signup',
  standalone: false,
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss']
})


export class SignupComponent implements OnInit {

  themeConfig: any;
  loaded: boolean = false;
  organizationDetails: any;
  loadedOrgDetails: boolean = false;

  signUpForm!: FormGroup;
  companyList: any[] = [];
  projectList: any[] = [];
  buildingList: any[] = [];
  unitList: any[] = [];
  btnLoading: boolean = false;
  isMobile = false;
  orgIdData: any;
  loading: boolean = true;
  isDisabled: boolean = true;
  isExpired: boolean = false;
  loginHost!: string;
  hide: boolean = true;

  config = {
    companyId: <ClientSideSingleSelectionConfig>{
      uri: '/entrance/get-companies',
      method: 'POST',
      limit: 250,
      isSearchable: true,
      isAllOption: false,
      isEnableRefreshMode: false,
      setFirstOption: true,
      ifLengthOnlyOne: true,
      additionalName: {
        keys: ['companyId'],
        separators: [' - ']
      }
    },
    projectId: <ClientSideSingleSelectionConfig>{
      uri: '/entrance/get-projects',
      method: 'POST',
      limit: 250,
      isSearchable: true,
      isAllOption: false,
      isEnableRefreshMode: false,
      setFirstOption: true,
      ifLengthOnlyOne: true,
      additionalName: {
        keys: ['code'],
        separators: [' - ']
      }
    },
    buildingId: <ClientSideSingleSelectionConfig>{
      uri: '/entrance/get-buildings',
      method: 'POST',
      limit: 250,
      isSearchable: true,
      isAllOption: false,
      isEnableRefreshMode: false,
      setFirstOption: true,
      ifLengthOnlyOne: true,
      additionalName: {
        keys: ['buildingPhaseCode'],
        separators: [' - ']
      }
    },
    unitId: <ServerSideSingleSelectionConfig>{
      uri: '/entrance/get-units',
      method: 'POST',
      limit: 50,
      isSearchable: true,
      isAllOption: false,
      isEnableRefreshMode: false,
      setFirstOption: true,
      ifLengthOnlyOne: true,
      additionalName: {
        keys: ['unitNumber'],
        separators: [' - ']
      }
    },
  }

  validationMessages = {
    name: [
      { type: 'required', message: 'Name is required' },
    ],
    userName: [
      { type: 'required', message: 'Username is required' },
    ],
    email: [
      { type: 'required', message: 'Email is required' },
    ],
    mobileNo: [
      { type: 'required', message: 'Mobile No. is required' },
    ],
    gender: [
      { type: 'required', message: 'Gender is required' },
    ],
    password: [
      { type: 'required', message: `Password is required` },
    ],
    companyId: [
      { type: 'required', message: 'Company is required' },
    ],
    projectId: [
      { type: 'required', message: 'Project is required' },
    ],
    buildingId: [
      { type: 'required', message: 'Building is required' },
    ],
    unitId: [
      { type: 'required', message: 'Unit is required' },
    ],
  }

  isHandset$: Observable<boolean> = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.TabletPortrait])
    .pipe(
      map(result => {
        console.log("== result ==", result);
        return result.matches;
      }),
      shareReplay()
    );

  constructor(
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private router: Router,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private translocoService: TranslocoService,
    private appService: AppService,
    private helper: HelperService
  ) { }

  ngOnInit() {
    this.signUpForm = new FormGroup({
      formUuid: new FormControl('', []),
      orgId: new FormControl(null),
      name: new FormControl('', [Validators.required]),
      userName: new FormControl('', [Validators.required]),
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(
          /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        ),
      ]),
      gender: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      mobileNo: new FormControl('', [Validators.required]),
      companyId: new FormControl(null, [Validators.required]),
      projectId: new FormControl(null, [Validators.required]),
      buildingId: new FormControl(null, [Validators.required]),
      unitId: new FormControl(null, [Validators.required]),
      isCheckedPrivacyPolicy: new FormControl(false, [Validators.required]),
    });

    this.loginHost = window.location.host;

    let organizationDetails$ = this.appService.getOrganisationDetailsForTheme(this.loginHost, 'domain').subscribe(r => {
      console.log('Theme Data:', r);
      this.organizationDetails = r?.data?.organisationDetails;
      console.log("=== Organisation Detail ===", this.organizationDetails);
      this.loadedOrgDetails = true;
      if (this.organizationDetails?.id > 0) {
        this.signUpForm?.get('orgId')?.setValue(this.organizationDetails?.id);
      }
    });

    this.isHandset$.subscribe(r => {
      console.log('IS HANDSET:', r);
      this.isMobile = r;
    });

    this.appService.themeConfig.subscribe(r => {
      this.themeConfig = r;
      this.loaded = true;
    });

    // this.appService.orgIdData.subscribe(r => {
    //   if (r > 1) {
    //     this.orgIdData = r;
    //     this.signUpForm.get('orgId')?.setValue(r);
    //   }
    // });

    let langData = localStorage.getItem('lang');
    if (langData) {
      this.translocoService.setActiveLang(langData);
    } else {
      this.translocoService.setActiveLang('en');
    }

    this.route.params.subscribe((r: any) => {
      console.log('Query: ', r);
      if (r?.uuid) {
        this.loading = true;
        this.authService.getSignUpFormDataByUUID(r.uuid).pipe(
          finalize(() => this.loading = false)
        )
        .subscribe((r: any) => {
          let data = r?.data?.formData || {};
          if(data?.isExpired){
            this.isExpired = true;
          }
          else{
            this.signUpForm.patchValue(data?.signUpDetails);
            this.signUpForm.get('formUuid')?.setValue(data?.uuid);
            // In this case disable orgId, companyId, projectId, buildingId, unitId
            setTimeout(() => {
              this.signUpForm.get('orgId')?.disable();
              if (data?.signUpDetails?.companyId) {
                this.signUpForm.get('companyId')?.disable();
              }
              if (data?.signUpDetails?.projectId) {
                this.signUpForm.get('projectId')?.disable();
              }
              if (data?.signUpDetails?.buildingId) {
                this.signUpForm.get('buildingId')?.disable();
              }
              if (data?.signUpDetails?.unitId) {
                this.signUpForm.get('unitId')?.disable();
              }
            }, 100);
          }
        }, err => this.helper.showHttpErrorMsg(err));
      }
      else{
        this.loading = false;
      }
    });

    this.loginHost = window.location.host;
    console.log("Location URL", window.location.host);
  }

  onSubmit() {
    if (this.signUpForm.valid) {
      console.log(this.signUpForm.getRawValue());
      this.btnLoading = true;
      this.authService.signup(this.signUpForm.getRawValue()).subscribe({
        next: r => {
          this.btnLoading = false;
          this.router.navigate([`/signup-confirmation/${r.data.user.id}`]);
          this.helper.showSuccessMsg(r.message, 'Success', 3500);
        },
        error: err => {
          this.btnLoading = false;
          this.helper.showHttpErrorMsg(err);
        }
      });
    }
  }

  changeActiveLanguage(langCode: string) {
    this.translocoService.setActiveLang(langCode);
    localStorage.setItem("lang", langCode);

  }

  onChange(isChecked: boolean) {
    if (isChecked) {
      this.isDisabled = false;
      console.log("checked");
    } else {
      this.isDisabled = true;
      console.log("not  checked");
    }
  }

  setDesktopBackground(degree = 90, primary = this.themeConfig?.bgAndFontColor.login.desktop.bg[0], secondary = this.themeConfig.bgAndFontColor.login.desktop.bg[1]) {
    if (this.isMobile) {
      return `linear-gradient(${degree}deg, ${this.themeConfig.bgAndFontColor.login.mobile.bg[0]} 10%, ${this.themeConfig.bgAndFontColor.login.mobile.bg[1]} 60%) !important`;
    }
    else {
      return `linear-gradient(${degree}deg, ${primary} 10%, ${secondary} 60%) !important`;
    }
  }

  setBorder(color: string, isBorder: boolean = this.themeConfig.bgAndFontColor.default.btnBorder) {
    if (isBorder) {
      return `1px solid ${color} !important`;
    }
    else {
      return ``;
    }

  }

  setBold(bold: boolean) {
    if (bold) {
      return 'bolder !important';
    }
    else {
      return '';
    }
  }

  setBorderRadius(isRadius: boolean, radius = '0px') {
    if (isRadius) {
      return '';
    }
    else {
      return radius;
    }
  }

  setHeaderBackground(degree = 90, primary = this.themeConfig.bgAndFontColor.login.desktop.bg[0], secondary = this.themeConfig.bgAndFontColor.login.desktop.bg[1]) {
    return `linear-gradient(${degree}deg, ${primary}, ${secondary}) !important`;
  }

}
